let livewireInitPromise = null;

function getLivewire() {
    return window.Livewire ?? null;
}

function waitForLivewire() {
    const livewire = getLivewire();
    if (livewire) {
        return Promise.resolve(livewire);
    }

    if (!livewireInitPromise) {
        livewireInitPromise = new Promise((resolve) => {
            document.addEventListener('livewire:init', () => {
                resolve(getLivewire());
            }, { once: true });
        });
    }

    return livewireInitPromise;
}

export function whenLivewireReady(callback) {
    const livewire = getLivewire();

    if (livewire) {
        callback(livewire);
        return;
    }

    document.addEventListener('livewire:init', () => {
        const instance = getLivewire();
        if (instance) {
            callback(instance);
        }
    }, { once: true });
}

export function livewireHook(name, handler) {
    let cleanup = null;
    let active = true;

    whenLivewireReady((livewire) => {
        if (!active) {
            return;
        }

        if (typeof livewire?.hook !== 'function') {
            return;
        }

        const maybeCleanup = livewire.hook(name, handler);
        if (typeof maybeCleanup === 'function') {
            cleanup = maybeCleanup;
        }
    });

    return () => {
        active = false;

        if (typeof cleanup === 'function') {
            cleanup();
        }
    };
}

export function livewireOn(name, handler) {
    let cleanup = null;
    let active = true;

    whenLivewireReady((livewire) => {
        if (!active) {
            return;
        }

        if (typeof livewire?.on !== 'function') {
            return;
        }

        const maybeCleanup = livewire.on(name, handler);
        if (typeof maybeCleanup === 'function') {
            cleanup = maybeCleanup;
        }
    });

    return () => {
        active = false;

        if (typeof cleanup === 'function') {
            cleanup();
        }
    };
}

export async function livewireDispatch(name, payload = {}) {
    const livewire = getLivewire();

    if (typeof livewire?.dispatch === 'function') {
        return livewire.dispatch(name, payload);
    }

    const resolvedLivewire = await waitForLivewire();

    if (typeof resolvedLivewire?.dispatch !== 'function') {
        return null;
    }

    return resolvedLivewire.dispatch(name, payload);
}

export function livewireFind(componentId) {
    const livewire = getLivewire();

    if (!componentId || typeof livewire?.find !== 'function') {
        return null;
    }

    return livewire.find(componentId);
}

export function livewireOff(name, callback = null) {
    const livewire = getLivewire();

    if (typeof livewire?.off !== 'function') {
        return;
    }

    if (callback) {
        livewire.off(name, callback);
        return;
    }

    livewire.off(name);
}

export function livewireAll() {
    const livewire = getLivewire();

    if (typeof livewire?.all !== 'function') {
        return [];
    }

    const components = livewire.all();

    return Array.isArray(components) ? components : [];
}

export function findClosestComponent(element) {
    const root = element?.closest?.('[wire\\:id]');
    const componentId = root?.getAttribute?.('wire:id');

    if (!componentId) {
        return null;
    }

    return livewireFind(componentId);
}

if (typeof window !== 'undefined') {
    window.LivewireBridge = {
        whenReady: whenLivewireReady,
        hook: livewireHook,
        on: livewireOn,
        dispatch: livewireDispatch,
        find: livewireFind,
        off: livewireOff,
        all: livewireAll,
        findClosestComponent,
    };
}

